import {
  type MediaCardMetadata,
  type MediaCardType,
} from '@wandb/weave/common/types/media';
import React, {useCallback, useMemo} from 'react';
import {useHistory} from 'react-router-dom';

import {mediaCompareModeActivated} from '../../services/analytics/productEvents';
import type * as PanelTypes from '../../state/views/panel/types';
import {type PartRefFromObjSchema} from '../../state/views/types';
import {type ClassLabelMap} from '../../util/media';
import {type RunWithHistoryAndMediaWandb} from '../MediaCard';
import {type KeyTypes} from './common';
import {
  getPanelContentSizing,
  type Layout,
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaultsAndMediaKeys,
  type SizingSettings,
} from './Component';
import type {GridData, SlotRegistry} from './layout/types';
import MediaPanelContent from './MediaPanelContent';
import {MediaBrowserModalContainer} from './modal/MediaBrowserModalContainer';
import {type TilePagination} from './tiles';
import {type StepMeta} from './types';
import {
  type CompareModeRunPair,
  getCompareModeEntryConfig,
} from './utils/compareEntryConfig';
import {useFullscreenPanelLink} from './utils/useFullscreenPanelLink';

type MediaBrowserPanelInnerProps = Omit<MediaBrowserPanelProps, ''> & {
  config: MediaConfigWithDefaultsAndMediaKeys;
  layout: Layout & {slots: SlotRegistry; gridData: GridData};
  mediaType: MediaCardType;
  firstMediaMetadata: MediaCardMetadata | null;
  currentStep: number;
  mediaBrowserWidth: number;
  mediaBrowserHeight: number;
  stepMeta: StepMeta;
  panelViewerRef: React.MutableRefObject<HTMLDivElement | null>;
  allMasks: ClassLabelMap;
  sizingSettings: SizingSettings;
  allMediaKeys: string[];
  keyTypes: KeyTypes;
  numericKeys: string[] | undefined;
  tilePagination: TilePagination | null;
  mediaBrowserRefCallback: (el: HTMLDivElement) => void;
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  isMaxStepLoading: boolean;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
};

const MediaBrowserPanelInnerComp = (props: MediaBrowserPanelInnerProps) => {
  const {
    config,
    configMode,
    disableRunLinks,
    layout,
    mediaType,
    firstMediaMetadata,
    mediaBrowserWidth,
    mediaBrowserHeight,
    stepMeta,
    panelViewerRef,
    sizingSettings,
    allMediaKeys,
    keyTypes,
    numericKeys,
    tilePagination,
    updateConfig,
    mediaBrowserRefCallback,
    customRunNames,
    isMaxStepLoading,
    runsWithMedia,
  } = props;

  const mediaBrowserSize = useMemo(() => {
    return {
      width: mediaBrowserWidth,
      height: mediaBrowserHeight - (tilePagination != null ? 30 : 0),
    };
  }, [tilePagination, mediaBrowserWidth, mediaBrowserHeight]);

  const {cardWidth, cardHeight} = useMemo(() => {
    return getPanelContentSizing({
      config,
      mediaType,
      mediaBrowserSize,
      sizingSettings,
      firstMediaMetadata,
    });
  }, [config, mediaType, mediaBrowserSize, sizingSettings, firstMediaMetadata]);

  const sliderConfig = useMemo(() => {
    return {
      snapToExistingStep: config.snapToExistingStep ?? true,
      stepStrideLength: config.stepStrideLength ?? 1,
    };
  }, [config.snapToExistingStep, config.stepStrideLength]);

  const mediaSliderKey = stepMeta.sliderKey;
  const history = useHistory();
  const fullscreenPanelLink = useFullscreenPanelLink();

  const handleCompareRunsFromModal = useCallback(
    (runPair: CompareModeRunPair, mediaKey?: string) => {
      if (config.mode === 'compare') {
        return;
      }

      mediaCompareModeActivated({
        entryPoint: 'fullscreen_modal',
        previousMode: config.mode,
      });
      const compareModeEntryConfig = getCompareModeEntryConfig(
        runPair.firstRunName,
        runPair.secondRunName,
        mediaKey
      );
      updateConfig(compareModeEntryConfig);
      window.scrollTo(0, 0);
      history.push(fullscreenPanelLink);
    },
    [fullscreenPanelLink, history, updateConfig, config.mode]
  );
  const onModalClose = useCallback(
    (finalStep: number, finalIndex: number) => {
      updateConfig({
        stepIndex: finalStep,
        mediaIndex: finalIndex,
      });
    },
    [updateConfig]
  );

  return (
    <MediaBrowserModalContainer
      runsWithMedia={runsWithMedia}
      stepMeta={stepMeta}
      sliderConfig={sliderConfig}
      onModalClose={onModalClose}
      onCompareRuns={handleCompareRunsFromModal}>
      <MediaPanelContent
        gridData={layout.gridData}
        slots={layout.slots}
        config={config}
        updateConfig={updateConfig}
        stepMeta={stepMeta}
        isMaxStepLoading={isMaxStepLoading}
        mediaBrowserSize={mediaBrowserSize}
        cardWidth={cardWidth}
        cardHeight={cardHeight}
        mediaType={mediaType}
        mediaSliderKey={mediaSliderKey}
        panelRef={props.panelRef}
        disableRunLinks={disableRunLinks}
        customRunNames={customRunNames}
        mediaBrowserRefCallback={mediaBrowserRefCallback}
        panelViewerRef={panelViewerRef}
        allMasks={props.allMasks}
        tilePagination={tilePagination}
        configMode={configMode}
        sizingSettings={sizingSettings}
        allMediaKeys={allMediaKeys}
        keyTypes={keyTypes}
        numericKeys={numericKeys}
        availableRuns={runsWithMedia}
        controlData={layout.controlData}
        rowData={layout.rowData}
      />
    </MediaBrowserModalContainer>
  );
};

export const MediaBrowserPanelInner = React.memo(MediaBrowserPanelInnerComp);
